import React, { useMemo, useState } from "react";
import { Img } from "react-image";
import { Rate, Modal } from "antd";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useAPI } from '../../config/API.js';
import TalentCandidate from "../../models/TalentCandidate.js";
import LoadingSpinner from "../../components/LoadingSpinner";
import UserImageCircle from "../../components/UserImageCircle";
import { useAuth } from "../../providers/AuthProvider.js";
import TalentReview, { TalentReviewSkeleton } from "./TalentReview.js";
import SpinLoader from "../../components/SpinLoader";


const DEFAULT_REVIEW_DATA = {
    rating: 5,
    title: '',
    notes: '',
}

const TalentReviewModal = ({ isOpen, onClose, imageSrc, talent, onUpdateTalent }) => {
    const queryClient = useQueryClient();

    const [isAddReviewOpen, setIsAddReviewOpen] = useState(false);
    const { auth } = useAuth();

    const [reviewData, setReviewData] = useState(DEFAULT_REVIEW_DATA);

    const { data, isLoading } = useAPI(['fetchTalentReviews', { talent_id: talent.id }], {
        enabled: isOpen
    })

    const sortedData = useMemo(() => {
        if (!data) return null;
        return [...data].sort((a, b) => {
            if ((auth.id === a.reviewer.id) && !a.project && !a.project_position) return -1;
            return 0
        })
    }, [data])



    const hasTalentReview = useMemo(() => {
        if (!sortedData?.length) return null;
        if ((sortedData[0].reviewer.id === auth.id) && !sortedData[0].project && !sortedData[0].project_position) {
            return true;
        }
        return false;
    }, [sortedData])


    const addReviewMutation = useMutation(
        ['addReviewMutation'],
        () => TalentCandidate.addReviewToTalent(talent.id, reviewData),
        {
            onSuccess: async res => {
                console.log(res, 'REPONSE IN USEMUATION')

                if (!res) {
                    return;
                }
                await queryClient.setQueryData(['fetchTalentReviews', { talent_id: talent.id }], prevData => {
                    const foundReview = prevData.find(r => r.id === res.id)

                    if (foundReview) {
                        return prevData.map(r => {
                            if (r.id === res.id) {
                                return res
                            }
                            return r
                        })
                    } else {
                        return [...prevData, res]
                    }
                })

                if (res.reviewable && onUpdateTalent) {
                    let {
                        avg_review_rating, reviews_count, id
                    } = res.reviewable;

                    await onUpdateTalent({ avg_review_rating, reviews_count, id })
                }

                return closeAndResetAddEditReviewModal();
            }
        }
    )

    const closeAndResetAddEditReviewModal = () => {
        setIsAddReviewOpen(false)
        setReviewData(DEFAULT_REVIEW_DATA);
    }

    const onSubmit = async () => {
        console.log('SUBMITTING FORM!! -')
        return addReviewMutation.mutate()
    }

    const handleChange = e => {
        setReviewData({ ...reviewData, [e.target.name]: e.target.value })
    }


    return (
        <Modal
            destroyOnClose
            open={isOpen}
            onCancel={onClose}
            closable={false}
            maskClosable
            title={null}
            footer={null}
            bodyStyle={{ padding: 0 }}
            // width={700}
            className="w-[700px]"
            zIndex={100}
        >
            <SpinLoader isLoading={addReviewMutation.isLoading} className="bg-white">
                {/* <div className="h-14 bg-primary flex items-center pl-4"> */}
                {/* <button
                        type="button"
                        onClick={onClose}
                        className="h-[22px]"
                    >
                        <div className="close-icon" />
                    </button> */}
                {/* </div> */}

                <div className="px-8 pt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-[22px]"
                    >
                        <div className="close-icon" />
                    </button>
                    <div className="flex justify-between">
                        <div className="pt-4">
                            <p className="text-xl font-semibold">{talent.firstNamePlusInitial()}</p>
                            <p className="text-xl font-semibold text-[#00278E]">Ratings and Notes</p>
                        </div>

                        <div>
                            <div className="relative -mt-6">
                                <Img
                                    loader={<div className="loading-user-image"><LoadingSpinner size="lg" /></div>}
                                    src={imageSrc}
                                    unloader={<UserImageCircle user={talent} hideTooltip={true} size={116} className="img-fluid img-grayscale h-28" />}
                                    className="img-fluid img-grayscale h-28 rounded-full"
                                />

                                <div className="h-14 w-14 rounded-full bg-[#F2AC39] flex items-center justify-center absolute bottom-0 -left-7">
                                    <p className="text-white text-xl mt-1">{talent.avg_review_rating?.toFixed(1) || '-'}</p>
                                </div>
                            </div>

                            <p className="text-sm pt-4">My Company</p>
                        </div>
                    </div>


                    <div className="flex flex-col gap-7 py-7">

                        {!data?.length && talent.reviews_count <= 0 && (
                            <p className="text-xl text-center pt-5">No Reviews Yet</p>
                        )}

                        {/* Show loading skeleton of up to 2 reviews */}
                        {isLoading && talent.reviews_count > 0 && Array(talent.reviews_count > 2 ? 2 : talent.reviews_count).fill().map((_, ind) => <TalentReviewSkeleton key={ind} isLoading />)}

                        {!isLoading && !!sortedData?.length && sortedData?.map(item => (
                            <TalentReview
                                key={item.id}
                                review={item}
                                onUpdateTalent={onUpdateTalent}
                                onEdit={editingReviewData => {
                                    setReviewData(editingReviewData)
                                    setIsAddReviewOpen(true)
                                }}
                            />
                        ))}
                    </div>


                    <div className="flex flex-row gap-3 mt-12 pb-8">
                        {!isLoading && !hasTalentReview && (
                            <button
                                className="btn btn-link w-[100px]"
                                // disabled={isUpdating}
                                onClick={() => setIsAddReviewOpen(true)}
                            >
                                Add Review
                            </button>
                        )}

                        <button
                            className="btn btn-outline-dark w-[100px] ml-auto"
                            // disabled={isUpdating}
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </SpinLoader>

            <Modal
                destroyOnClose
                open={isAddReviewOpen}
                onCancel={() => {
                    setIsAddReviewOpen(false)
                    setReviewData(DEFAULT_REVIEW_DATA);
                }}
                closable={false}
                maskClosable
                title={null}
                footer={null}
                bodyStyle={{ padding: 0, marginTop: 40 }}
                className="w-[460px]"
            >
                <div className="bg-white">
                    {/* <div className="h-14 bg-primary flex items-center pl-4">
                        <button
                            type="button"
                            // onClick={onClose}
                            onClick={() => {
                                setIsAddReviewOpen(false)
                                setReviewData(DEFAULT_REVIEW_DATA);
                            }}
                            className="h-[22px]"
                        >
                            <div className="close-icon" />
                        </button>
                    </div> */}

                    <div className="px-8 pt-6">

                        <div className="flex justify-between gap-4">
                            <div>
                                <p className="text-xl">Write a Review</p>
                                <p className="font-semibold">For {talent.firstNamePlusInitial()}</p>
                            </div>

                            <button
                                type="button"
                                // onClick={onClose}
                                onClick={() => {
                                    setIsAddReviewOpen(false)
                                    setReviewData(DEFAULT_REVIEW_DATA);
                                }}
                                className="h-[22px]"
                            >
                                <div className="close-icon" />
                            </button>
                        </div>

                        <div className="fle flex-col justify-between space-between pt-6">
                            <div>
                                <p className="-mb-3">Rating</p>
                                <Rate
                                    allowHalf
                                    className="flex"
                                    style={{ fontSize: 50 }}
                                    onChange={val => handleChange({ target: { name: 'rating', value: val < 1 ? 1 : val } })}
                                    value={reviewData.rating}
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="title">
                                    <p>Quick Note</p>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        required
                                        className="w-full border border-gray-400 p-2"
                                        onChange={handleChange}
                                        value={reviewData.title}
                                    />
                                </label>
                            </div>

                            <div>
                                <label htmlFor="notes">
                                    <p>Details</p>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        required
                                        className="w-full h-[100px] border border-gray-400 p-2"
                                        // onChange={e => setNotes(e.target.value)}
                                        onChange={handleChange}
                                        value={reviewData.notes}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="flex flex-row gap-3 justify-end mt-12 pb-8">
                            <button
                                className="btn btn-link w-[100px]"
                                // disabled={isUpdating}
                                onClick={() => {
                                    setIsAddReviewOpen(false)
                                    setReviewData(DEFAULT_REVIEW_DATA);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary px-6 self-end"
                                // disabled={isUpdating}
                                onClick={onSubmit}
                            >
                                Submit Review
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

        </Modal>
    )
}

export default TalentReviewModal;